import { Trans } from '@lingui/react/macro';
import { type UIEvent, useLayoutEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router';

import { useAudienceOptionsPrefetch } from '@/api/audience';
import { useCurrentInstance } from '@/api/organization';
import Error404 from '@/components/errors/Error404';
import PageError from '@/components/errors/PageError';
import { GlobalLoader } from '@/components/GlobalLoader';
import { Layout } from '@/components/page';
import AISearch from '@/components/page/ai/AISearch';
import { SidebarProvider, SidebarTrigger } from '@/components/Sidebar';
import { PAGE_WIDTH_NO_SIDEBAR } from '@/constants/dimensions';
import { cn } from '@/lib/utils';
import { useMe } from '@/providers/User';

import LoginAsNotification from './LoginAsNotification';
import NotificationWrapper from './NotificationWrapper';
import { Sidebar } from './Sidebar';
import TagNotFoundNotification from './TagNotFoundNotification';

const { Content } = Layout;
const ASSISTANT_LAYOUT_CLASS = 'c99-assistant-layout';
const ASSISTANT_FRAME_CLASS = 'c99-assistant-frame';
const ASSISTANT_SPACER_CLASS = 'c99-assistant-spacer';

export const Home = () => {
  const contentRef = useRef<HTMLElement>(null);
  const contentSpacerRef = useRef<HTMLDivElement>(null);
  const searchHeaderRef = useRef<HTMLDivElement>(null);
  const contentScrollTopRef = useRef(0);
  const { pathname } = useLocation();
  const [searchHeaderState, setSearchHeaderState] = useState({ pathname, isCompact: false });
  if (searchHeaderState.pathname !== pathname) {
    setSearchHeaderState({ pathname, isCompact: false });
  }
  const isSearchCompact = searchHeaderState.isCompact;
  const user = useMe();
  const { instance, isLoading, error } = useCurrentInstance();
  useAudienceOptionsPrefetch();
  const foreignSuperuserInstance = user?.isC99Superuser && user.currentOrg.isForeign;

  useLayoutEffect(() => {
    contentScrollTopRef.current = 0;
    contentRef.current?.scrollTo(0, 0);
  }, [pathname]);

  useLayoutEffect(() => {
    const content = contentRef.current;
    const contentSpacer = contentSpacerRef.current;
    const searchHeader = searchHeaderRef.current;
    const fields = searchHeader?.querySelector<HTMLElement>('.c99-assistant-fields');
    if (!content || !contentSpacer || !fields) {
      return;
    }

    const syncSpacer = () => {
      const paddingUnits = Number(
        getComputedStyle(fields).getPropertyValue('--assistant-expanded-padding-units'),
      );
      // Outer py-5 minus compact py-2 leaves six units, plus both expanded field insets.
      contentSpacer.style.setProperty(
        '--assistant-collapse-height',
        `calc(var(--spacing) * ${6 + paddingUnits * 2})`,
      );
    };
    syncSpacer();
    content.scrollTop = contentScrollTopRef.current;
    const observer = new ResizeObserver(syncSpacer);
    observer.observe(content.parentElement!);
    return () => observer.disconnect();
  }, [isSearchCompact, isLoading, pathname]);

  const handleContentScroll = (event: UIEvent<HTMLElement>): void => {
    const { scrollTop } = event.currentTarget;
    const isCompact = scrollTop > 0;
    contentScrollTopRef.current = scrollTop;
    setSearchHeaderState((previous) =>
      previous.pathname === pathname && previous.isCompact === isCompact
        ? previous
        : { pathname, isCompact },
    );
  };

  if (isLoading) {
    return <GlobalLoader />;
  }

  if (error) {
    if (error.is404()) {
      return (
        <Error404
          title={<Trans>Unable to load the instance</Trans>}
          subtitle={
            <Trans>
              This can happen if the instance doesn't exist or you don't have access to it.
              <p>
                If you don't have access to an instance, please contact your administrator for an
                invite. Check that you typed the address correctly, go back to your previous page or
                return to the home page.
              </p>
            </Trans>
          }
        />
      );
    }
    return (
      <PageError
        message={<Trans>Unable to load the instance</Trans>}
        detail={
          <Trans>
            Please refresh the page. If this error continues to happen please contact support.
          </Trans>
        }
      />
    );
  }

  return (
    <SidebarProvider
      mobileBreakpoint={PAGE_WIDTH_NO_SIDEBAR}
      className="[--sidebar-width-mobile:180px] [--sidebar-width:180px] 2xl:[--sidebar-width-mobile:200px] 2xl:[--sidebar-width:200px]"
    >
      <Layout className="h-screen flex-row">
        <Sidebar />
        <Layout
          className={cn(
            ASSISTANT_LAYOUT_CLASS,
            'min-w-0 flex-1 transition-[--c99-assistant-progress] duration-100 ease-out motion-reduce:transition-none',
            isSearchCompact ? '[--c99-assistant-progress:1]' : '[--c99-assistant-progress:0]',
          )}
        >
          <header className="z-20 shrink-0 bg-white">
            <div
              ref={searchHeaderRef}
              className={cn(
                ASSISTANT_FRAME_CLASS,
                'flex items-center gap-2 [--assistant-expanded-inline-units:8] max-lg:[--assistant-surface-start-outset-units:16] max-sm:[--assistant-expanded-inline-units:4]',
              )}
            >
              <SidebarTrigger
                data-testid="sidebar-toggle"
                className="relative z-10 shrink-0 lg:hidden"
                size="icon-large"
                inverse={isSearchCompact}
              />
              <div className="min-w-0 flex-1">
                <AISearch compact={isSearchCompact} />
              </div>
            </div>
            {foreignSuperuserInstance && (
              <LoginAsNotification instanceId={user?.currentOrg.instanceId} />
            )}
            {!instance?.tagInstallationDate && <TagNotFoundNotification />}
            <NotificationWrapper />
          </header>
          <Content
            ref={contentRef}
            className="flex flex-col overflow-auto bg-white [overflow-anchor:none]"
            onScroll={handleContentScroll}
          >
            <div
              ref={contentSpacerRef}
              className={cn(ASSISTANT_SPACER_CLASS, 'shrink-0')}
              aria-hidden="true"
            />
            <Outlet />
          </Content>
        </Layout>
      </Layout>
    </SidebarProvider>
  );
};
